import { CommonModule } from "@angular/common";
import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { UtilityService } from 'src/app/services/utility.service';
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { NgForm } from "@angular/forms";
import { DrupalRESTService } from "src/app/services/drupal-rest.service";
import { Router } from "@angular/router";
import { saveAs } from 'file-saver';
import moment from "moment";
import { ViewTableComponent } from "../../base/view-table/view-table.component";
import { StudentDashboardWrapperComponent } from "../student-dashboard-wrapper/student-dashboard-wrapper.component";
import { ComponentType } from "@angular/cdk/overlay";
import { ConfirmDialogComponent } from "../../utility/confirm-dialog/confirm-dialog.component";
import { MatSnackBar } from "@angular/material/snack-bar";
import { PrintService } from "src/app/services/print.service";
import { StudentInquiryEntityComponent } from "../../forms/student-inquiry-entity/student-inquiry-entity.component";
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-students-list',
  templateUrl: './students-list.component.html',
  styleUrls: [
    './students-list.component.css',
    '../../../app.component.css'
  ]
})

export class StudentsListComponent extends ViewTableComponent implements OnInit {

  currentPage: number;
  StudentDashboardWrapperComponent = StudentDashboardWrapperComponent;
  StudentInquiryEntityComponent = StudentInquiryEntityComponent;
  Object = Object; // Expose Object for template use
  isArchived: boolean = false;
  activeLink: string = 'Active Students';
  links = [
    { linkTitle: 'Active Students', value: false },
    { linkTitle: 'Retired Students', value: true }
  ];
  lastChecked: string = '';

  override ngOnInit() {
    super.ngOnInit();
    this._printService = new PrintService(this._drupalRESTService, this._authService);
  }

  override onPageSizeOptions() {
    return [10, 25, 50, 100, 250];
  }

  override pageChanged(event: PageEvent) {
    // Forward page AND items_per_page to the new ami_student_list endpoint.
    this.paginationTotalPages = event.pageSize;
    this.updateRouterParams({
      ...this.routeParams,
      page: event.pageIndex.toString(),
      items_per_page: event.pageSize.toString(),
    });
  }

  override displayedColumns: string[] = [
    'field_last_name_value',
    'field_first_name_value',
    'field_student_department_target_id',
    // 'field_last_lesson_date_day_value',
    'field_last_lesson_taken_value',
    'field_upcoming_lesson_value',
    'field_total_lessons_enrolled_value',
    'field_total_lessons_taken_value',
    'field_total_lessons_remaining_value',
    'field_total_lessons_paid_ahead_value',
    'instructor',
    'field_email_value',
    'field_cell_phone_value',
    'dashboard',
    'edit'
  ];

  isExporting: boolean = false;

  private _printService: PrintService;

  // A student account can reference multiple contacts (e.g. a couple sharing
  // one enrollment). The backend returns one row per account with the primary
  // contact in the headline scalar fields and every contact in contacts[].
  // These helpers drive the stacked-inline rendering of the secondary contacts.
  //
  // Some accounts carry orphaned/placeholder contact rows with blank names; we
  // skip those so they don't render as empty muted lines or blank menu items.
  private namedContacts(element: any): any[] {
    const contacts = element?.contacts ?? [];
    return contacts.filter((c: any) => (c.name ?? '').trim() !== '');
  }

  secondaryContacts(element: any): any[] {
    // Robust even if the primary weren't delta 0: exclude by contact id.
    return this.namedContacts(element)
      .filter((c: any) => String(c.contact_id) !== String(element.link));
  }

  // Whether the account has more than one NAMED contact (drives the people vs
  // person dashboard icon).
  hasMultipleContacts(element: any): boolean {
    return this.namedContacts(element).length > 1;
  }

  // Day-granularity key for an upcoming-lesson value, matching the column's
  // `| date` display. Used to suppress a redundant second line when both
  // partners' next lesson falls on the same day (e.g. "Apr 17, 2026" twice).
  private upcomingLessonDay(value: any): string {
    if (!value) {
      return '';
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? String(value) : d.toDateString();
  }

  // Secondary contacts whose next lesson differs (by day) from the primary's,
  // so the Next Lesson column only stacks a second line when it adds info.
  secondaryUpcomingLessons(element: any): any[] {
    const primaryDay = this.upcomingLessonDay(element?.field_upcoming_lesson);
    return this.secondaryContacts(element)
      .filter((c: any) => this.upcomingLessonDay(c.upcoming_lesson) !== primaryDay);
  }

  override getData(params: { parameter: string; value: string; }[]) {
    this.ShowProgressBar = true;
    this.queryParams = params;

    // New ami_student_list module: one endpoint with archived=0|1. The
    // legacy /api/v1/students and /api/v1/student-list-retired routes stay
    // live during the transition (in case any external integration depends
    // on them) but the UI is on the new path.
    const baseEndpoint = "/api/v1/student-list";
    let filters = `?archived=${this.isArchived ? 1 : 0}`;

    if (params) {
      let hasActiveFilter = false;
      let hasInactiveFilter = false;
      let hasNFAFilter = false;
      let hasPaidFinishedFilter = false;
      let hasSortBy = false;
      let hasSortOrder = false;

      // First pass - identify which filters are present
      for (const property in params) {
        if (params[property]?.['parameter'] === 'Active') hasActiveFilter = true;
        if (params[property]?.['parameter'] === 'Inactive') hasInactiveFilter = true;
        if (params[property]?.['parameter'] === 'NFA') hasNFAFilter = true;
        if (params[property]?.['parameter'] === 'paid_finished') hasPaidFinishedFilter = true;
        if (params[property]?.['parameter'] === 'sort_by') hasSortBy = true;
        if (params[property]?.['parameter'] === 'sort_order') hasSortOrder = true;
      }

      // Only apply the default sort if the caller didn't provide one.
      if (!hasSortBy) filters += "&sort_by=field_last_name_value";
      if (!hasSortOrder) filters += "&sort_order=ASC";

      // Second pass - apply filters with logic to handle conflicts
      // Handle Active/Inactive filters (mutually exclusive)
      if (hasActiveFilter && !hasInactiveFilter) {
        filters += "&field_last_lesson_taken_value=" + moment().subtract(30, 'days').format('YYYY-MM-DD') + "--";
      } else if (!hasActiveFilter && hasInactiveFilter) {
        filters += "&field_last_lesson_taken_value=" + "--" + moment().subtract(30, 'days').format('YYYY-MM-DD');
      }

      // Handle NFA filter
      if (hasNFAFilter) {
        filters += "&field_nfa=1";
        filters += "&field_total_lessons_remaining_value[value]=0";
        filters += "&field_total_lessons_remaining_value[min]=";
        filters += "&field_total_lessons_remaining_value[max]=";
        filters += "&field_total_lessons_remaining_value_op=>";
      }

      // Handle paid_finished filter (only if NFA is not active to avoid conflicts)
      if (hasPaidFinishedFilter && !hasNFAFilter) {
        filters += "&field_paid_finished=true";
        filters += "&paid_finished=true"; // Add the checkbox parameter as well
        filters += "&field_total_lessons_paid_ahead_value=0";
        filters += "&field_total_lessons_remaining_value[value]=0";
        filters += "&field_total_lessons_remaining_value[min]=";
        filters += "&field_total_lessons_remaining_value[max]=";
        filters += "&field_total_lessons_remaining_value_op==";
      }

      // Process other parameters that aren't special checkbox filters
      for (const property in params) {
        const param = params[property];
        if (param &&
          param.parameter !== 'Active' &&
          param.parameter !== 'Inactive' &&
          param.parameter !== 'NFA' &&
          param.parameter !== 'paid_finished' &&
          param.value) {
          filters += `&${param.parameter}=${encodeURIComponent(param.value)}`;
        }
      }
    }

    const endpoint = baseEndpoint + filters;

    this._drupalRESTService.httpGET(endpoint, params)
      .subscribe({
        next: (data) => {
          this.ShowProgressBar = false;
          this.dataSource = new MatTableDataSource(data['results']);
          if (data['results']) {
            this.setupPagination(data, params);
          }
        },
        error: error => this.handleError(error)
      });
  }

  setStudentArchive(student_account_id: number, value: boolean) {
    let body = {
      field_archive: value ? 1 : 0,
    };
    this._entityRESTService.patchEntity('student_accounts', 'student_account', student_account_id, body)
      .subscribe({
        next: () => {
          this.getData(this.queryParams);
          const message = value ? 'Student archived successfully' : 'Student unarchived successfully';
          this._snackBar.open(message, 'Close', { duration: 3000 });
        },
        error: (error) => {
          LoggerService.error('Error updating student archive status:', error);
          this._snackBar.open('Error updating student status', 'Close', { duration: 3000 });
        }
      });
  }

  deleteStudent(student_account_id: number) {
    const isSuperAdmin = this._permissionsService.isSuperAdmin();
    const dialogConfig = isSuperAdmin
      ? {
          title: 'Delete Student',
          message: 'Are you sure you want to permanently delete this student? This will hard-delete the student account and cascade-delete all related data. This action cannot be undone.',
          confirmText: 'Delete',
          cancelText: 'Cancel'
        }
      : {
          title: 'Archive Student',
          message: 'Are you sure you want to archive this student? The student and all related data will be preserved but hidden from active views.',
          confirmText: 'Archive',
          cancelText: 'Cancel'
        };

    const dialogRef = this._dialogService.open(
      ConfirmDialogComponent,
      this._dialogService.dialogConfig('confirm', dialogConfig)
    );

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      if (isSuperAdmin) {
        this._drupalRESTService.httpDELETE(`/api_entity/eck/student_accounts/student_account/${student_account_id}`)
          .subscribe({
            next: () => {
              this.getData(this.queryParams);
              this._snackBar.open('Student deleted successfully', 'Close', { duration: 3000 });
            },
            error: (error) => {
              LoggerService.error('Error deleting student:', error);
              this._snackBar.open('Error deleting student', 'Close', { duration: 3000 });
            }
          });
      } else {
        this.setStudentArchive(student_account_id, true);
      }
    });
  }

  prepareExportParams(params: { parameter: string; value: string; }[]): any {
    return params?.reduce((acc, curr) => {
      acc[curr.parameter] = curr.value;
      return acc;
    }, {}) || {};
  }

  exportStudentListReport() {
    this.isExporting = true;

    const baseEndpoint = "/api/v1/student-list/export";
    let filters = `?archived=${this.isArchived ? 1 : 0}`;

    if (this.queryParams) {
      let hasActiveFilter = false;
      let hasInactiveFilter = false;
      let hasNFAFilter = false;
      let hasPaidFinishedFilter = false;
      let hasSortBy = false;
      let hasSortOrder = false;

      // First pass - identify which filters are present
      for (const property in this.queryParams) {
        if (this.queryParams[property]?.['parameter'] === 'Active') hasActiveFilter = true;
        if (this.queryParams[property]?.['parameter'] === 'Inactive') hasInactiveFilter = true;
        if (this.queryParams[property]?.['parameter'] === 'NFA') hasNFAFilter = true;
        if (this.queryParams[property]?.['parameter'] === 'paid_finished') hasPaidFinishedFilter = true;
        if (this.queryParams[property]?.['parameter'] === 'sort_by') hasSortBy = true;
        if (this.queryParams[property]?.['parameter'] === 'sort_order') hasSortOrder = true;
      }

      // Only apply the default sort if the caller didn't provide one.
      if (!hasSortBy) filters += "&sort_by=field_last_name_value";
      if (!hasSortOrder) filters += "&sort_order=ASC";

      // Second pass - apply filters with logic to handle conflicts
      // Handle Active/Inactive filters (mutually exclusive)
      if (hasActiveFilter && !hasInactiveFilter) {
        filters += "&field_last_lesson_taken_value=" + moment().subtract(30, 'days').format('YYYY-MM-DD') + "--";
      } else if (!hasActiveFilter && hasInactiveFilter) {
        filters += "&field_last_lesson_taken_value=" + "--" + moment().subtract(30, 'days').format('YYYY-MM-DD');
      }

      // Handle NFA filter
      if (hasNFAFilter) {
        filters += "&field_nfa=1";
        filters += "&field_total_lessons_remaining_value[value]=0";
        filters += "&field_total_lessons_remaining_value[min]=";
        filters += "&field_total_lessons_remaining_value[max]=";
        filters += "&field_total_lessons_remaining_value_op=>";
      }

      // Handle paid_finished filter (only if NFA is not active to avoid conflicts)
      if (hasPaidFinishedFilter && !hasNFAFilter) {
        filters += "&field_paid_finished=true";
        filters += "&paid_finished=true"; // Add the checkbox parameter as well
        filters += "&field_total_lessons_paid_ahead_value=0";
        filters += "&field_total_lessons_remaining_value[value]=0";
        filters += "&field_total_lessons_remaining_value[min]=";
        filters += "&field_total_lessons_remaining_value[max]=";
        filters += "&field_total_lessons_remaining_value_op==";
      }

      // Process other parameters that aren't special checkbox filters
      for (const property in this.queryParams) {
        const param = this.queryParams[property];
        if (param &&
          param.parameter !== 'Active' &&
          param.parameter !== 'Inactive' &&
          param.parameter !== 'NFA' &&
          param.parameter !== 'paid_finished' &&
          param.value) {
          filters += `&${param.parameter}=${encodeURIComponent(param.value)}`;
        }
      }
    }

    // Convert filters to parameter array format for downloadCSVDrupalViews
    const paramArray = this.convertFiltersToParamArray(filters);

    // Add _format=csv to params to prevent automatic _format=json from being added
    paramArray.push({ parameter: '_format', value: 'csv' });

    // Call the custom download method
    try {
      this.downloadStudentCSV(baseEndpoint, 'student_list', paramArray);
    } finally {
      this.isExporting = false;
    }
  }

  // Custom download method that doesn't add _format to URL (since it's already in params)
  private downloadStudentCSV(route: string, filename: string, params: any) {
    this.isLoading = true;

    // Don't add _format=csv to route since it's already in params
    this._drupalRESTService.downloadFile(route, params)
      .subscribe({
        next: (response: any) => {
          let dataType = response.type;
          let binaryData = [];
          binaryData.push(response.body);
          let downloadLink = document.createElement('a');
          downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
          if (filename) {
            downloadLink.setAttribute('download', filename + '.csv');
          }
          document.body.appendChild(downloadLink);
          downloadLink.click();
          downloadLink.remove();
          this.isLoading = false;
        },
        error: (error) => {
          LoggerService.error('Error downloading CSV:', error);
          this._snackBar.open('Error downloading student list', 'Close', { duration: 3000 });
          this.isLoading = false;
        }
      });
  }

  private convertFiltersToParamArray(filters: string): { parameter: string; value: string; }[] {
    const paramArray: { parameter: string; value: string; }[] = [];
    const seenParams = new Set<string>(); // Track seen parameters to avoid duplicates

    // Remove the leading '?' and split by '&'
    const queryString = filters.startsWith('?') ? filters.substring(1) : filters;
    const params = queryString.split('&');

    for (const param of params) {
      if (param) {
        // Handle parameters with = in the value (like URLs)
        const equalIndex = param.indexOf('=');
        if (equalIndex !== -1) {
          const parameter = param.substring(0, equalIndex);
          const value = param.substring(equalIndex + 1);
          if (parameter && !seenParams.has(parameter)) {
            seenParams.add(parameter);
            // Don't decode here - let the HTTP service handle encoding
            paramArray.push({ parameter, value });
          }
        }
      }
    }

    return paramArray;
  }

  printStudentList() {
    const rows: any[] = this.dataSource?.data ?? [];

    const filterSummary = this.buildPrintFilterSummary();
    const reportTitle = this.isArchived ? 'Retired Students' : 'Student List';

    const escape = (v: any) => {
      const s = v === null || v === undefined ? '' : String(v);
      return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    };

    const formatDate = (v: any) => {
      if (!v) return '';
      const m = moment(v);
      return m.isValid() ? m.format('M/D/YYYY') : '';
    };

    const formatPhone = (v: any) => {
      if (!v) return '';
      const digits = String(v).replace(/\D/g, '');
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      if (digits.length === 11 && digits.startsWith('1')) {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
      }
      return String(v);
    };

    const headers = [
      'Last Name', 'First Name', 'Department',
      'Last Lesson', 'Next Lesson',
      'Enrolled', 'Taken', 'Remaining', 'Paid Ahead',
      'Instructor', 'Email', 'Phone'
    ];

    const headerHtml = headers.map(h => `<th>${escape(h)}</th>`).join('');

    const bodyHtml = rows.map((r: any) => {
      const paidAhead = r.field_total_lessons_paid_ahead;
      const paidAheadStyle = (paidAhead !== null && paidAhead !== undefined && parseFloat(paidAhead) < 0)
        ? ' style="color:#c00;"'
        : '';
      // Stack every named contact so a couple reads naturally in one row.
      // Skip orphaned/placeholder contacts that have a blank name.
      const contacts = (r.contacts ?? []).filter((c: any) => (c.name ?? '').trim() !== '');
      const lastNames = contacts.map((c: any) => c.last_name).filter((x: string) => (x ?? '').trim());
      const firstNames = contacts.map((c: any) => c.first_name).filter((x: string) => (x ?? '').trim());
      const lastCell = lastNames.length ? lastNames.map(escape).join('<br>') : escape(r.field_last_name);
      const firstCell = firstNames.length ? firstNames.map(escape).join('<br>') : escape(r.field_first_name);
      return `<tr>
        <td>${lastCell}</td>
        <td>${firstCell}</td>
        <td>${escape(r.department)}</td>
        <td class="nowrap">${escape(formatDate(r.field_last_lesson_taken))}</td>
        <td class="nowrap">${escape(formatDate(r.field_upcoming_lesson))}</td>
        <td class="num">${escape(+r.field_total_lessons_enrolled || 0)}</td>
        <td class="num">${escape(r.field_total_lessons_taken_all ?? '')}</td>
        <td class="num">${escape(r.field_total_lessons_remaining ?? '')}</td>
        <td class="num"${paidAheadStyle}>${escape(paidAhead ?? '')}</td>
        <td>${escape(r.instructor)}</td>
        <td>${escape(r.email)}</td>
        <td class="nowrap">${escape(formatPhone(r.phone))}</td>
      </tr>`;
    }).join('');

    const summaryHtml = filterSummary
      ? `<div class="filter-summary"><strong>Filters:</strong> ${escape(filterSummary)}</div>`
      : '';

    const countHtml = `<div class="row-count">${rows.length} student account${rows.length === 1 ? '' : 's'}</div>`;

    const content = `
      <h1 class="report-title">${escape(reportTitle)}</h1>
      ${summaryHtml}
      ${countHtml}
      <table class="report-table">
        <thead><tr>${headerHtml}</tr></thead>
        <tbody>${bodyHtml}</tbody>
      </table>
    `;

    const styles = `
      @page { size: letter landscape; margin: 0.5in; }
      .report-table { width: 100%; border-collapse: collapse; font-size: 10px; }
      .report-table th, .report-table td { border: 1px solid #ccc; padding: 3px 5px; }
      .report-table th { background: #f0f0f0; }
      .report-table td.num { text-align: right; }
      .report-table td.nowrap { white-space: nowrap; }
      .filter-summary { margin: 4px 0; font-size: 11px; }
      .row-count { margin: 4px 0 8px 0; font-size: 11px; color: #555; }
    `;

    this._printService.print(content, {
      title: reportTitle,
      styles,
      showSchoolName: true,
      promptPrint: true
    });
  }

  private buildPrintFilterSummary(): string {
    const params = this.queryParams || [];
    const parts: string[] = [];
    const labelMap: { [k: string]: string } = {
      Active: 'Active',
      Inactive: 'Inactive',
      NFA: 'No Future Appointments',
      paid_finished: 'Paid Finished'
    };

    for (const p of params) {
      if (!p || !p.parameter) continue;
      if (labelMap[p.parameter]) {
        parts.push(labelMap[p.parameter]);
        continue;
      }
      if (p.parameter === 'instructor' && p.value) {
        const instructors = this._fieldsService.instructorData() || [];
        const match = instructors.find((i: any) => String(i.uid) === String(p.value));
        parts.push(`Instructor: ${match ? match.value : p.value}`);
        continue;
      }
      if (p.parameter === 'student_department_id' && p.value && p.value !== 'All') {
        const depts = (this as any).departmentData || [];
        const match = depts.find((d: any) => String(d.key) === String(p.value));
        parts.push(`Department: ${match ? match.value : p.value}`);
        continue;
      }
      if (p.parameter === 'id' && p.value) {
        parts.push(`Student ID: ${p.value}`);
        continue;
      }
      if (p.parameter === 'email' && p.value) {
        parts.push(`Email: ${p.value}`);
        continue;
      }
      if (p.parameter === 'phone' && p.value) {
        parts.push(`Phone: ${p.value}`);
        continue;
      }
    }

    if (this.isArchived) parts.unshift('Retired');
    return parts.join(' • ');
  }

  onTabChange(link: { linkTitle: string, value: boolean }) {
    this.activeLink = link.linkTitle;
    this.isArchived = link.value;
    this.getData(this.queryParams);
  }

  /**
   * The manage_accounts icon beside the Student (Contact) search opens that
   * contact's dashboard. The box only holds a contact id once an autocomplete
   * option was picked; free text (a name that does not exist yet) must never
   * be passed as the id, because the dashboard then opens empty and saves made
   * from it attach to nothing (orphan contacts, PATCHes to .../undefined).
   */
  openStudentDashboardFromSearch(typed: string): void {
    const value = (typed ?? '').trim();
    if (!value) {
      this._snackBar.open('Type a student name and pick them from the list first.', 'Close', { duration: 4000 });
      return;
    }

    // A picked option leaves the contact id in the box; otherwise accept an
    // exact name match from the current suggestions.
    let contactId: string | null = /^\d+$/.test(value) ? value : null;
    if (!contactId) {
      const match = (this.autoCompleteStudentContactOptions || []).find(
        (option: any) => String(option?.label ?? '').trim().toLowerCase() === value.toLowerCase()
      );
      contactId = match ? String(match.value) : null;
    }

    if (!contactId) {
      this._snackBar.open(`No student matches "${value}". Pick a name from the list, or use Add Student to create one.`, 'Close', { duration: 5000 });
      return;
    }

    this.openDialog(StudentDashboardWrapperComponent, 'largerScreenDashboard', { student_id: contactId });
  }

  // Override the onSubmit method to handle the "None" selection for instructor
  override onSubmit(form: NgForm): void {
    // Check if instructor field exists and is explicitly set to null or empty string
    const formValues = form.form.value;
    const hasInstructorField = 'instructor' in formValues;

    // Call the parent onSubmit to process most of the form
    super.onSubmit(form);

    // If instructor field exists but was filtered out because it's empty/null,
    // we need to explicitly remove any existing instructor filter
    if (hasInstructorField && (formValues.instructor === null || formValues.instructor === '')) {
      // Find and remove any existing instructor parameter
      this.queryParams = this.queryParams.filter(param => param.parameter !== 'instructor');

      // Update route params too by removing the instructor parameter
      if (this.routeParams && 'instructor' in this.routeParams) {
        const { instructor, ...restParams } = this.routeParams;
        this.routeParams = restParams;

        // Update the router params
        this.updateRouterParams(this.routeParams);
      }
    }

    // Get the data with the updated parameters
    this.getData(this.queryParams);
  }

  onCheckboxChange(form: NgForm) {
    // Get current form values
    const formValues = form.form.value;

    // Determine which checkbox was just checked
    if (formValues.Active) {
      this.lastChecked = 'Active';
    } else if (formValues.Inactive) {
      this.lastChecked = 'Inactive';
    } else if (formValues.NFA) {
      this.lastChecked = 'NFA';
    } else if (formValues.paid_finished) {
      this.lastChecked = 'paid_finished';
    }

    // Handle mutually exclusive checkboxes
    if (formValues.Active && formValues.Inactive) {
      // Either disable one checkbox or prioritize one over the other
      // For example, if Active is newly checked, uncheck Inactive
      if (this.lastChecked === 'Active') {
        form.form.patchValue({ Inactive: false });
      } else {
        form.form.patchValue({ Active: false });
      }
    }

    // Handle conflicts between NFA and paid_finished
    if (formValues.NFA && formValues.paid_finished) {
      if (this.lastChecked === 'NFA') {
        form.form.patchValue({ paid_finished: false });
      } else {
        form.form.patchValue({ NFA: false });
      }
    }

    // Now submit the form with the corrected values
    this.onSubmit(form);
  }

  override setupPagination(data: any, params: { parameter: string; value: string; }[]) {
    const pageParam = params.find(p => p.parameter === 'page');
    const sizeParam = params.find(p => p.parameter === 'items_per_page');
    // Honor the URL's items_per_page (or default to 25, which matches the
    // default the new ami_student_list backend applies).
    const pageSize = sizeParam ? parseInt(sizeParam.value, 10) : 25;
    this.paginationTotalPages = pageSize;
    this.paginationIndex = pageParam ? parseInt(pageParam.value, 10) : 0;
    if (this.paginator) {
      this.paginator.length = Number(data?.['count'] ?? 0);
      this.paginator.pageSize = pageSize;
      this.paginator.pageIndex = this.paginationIndex;
    }
    // DO NOT call updateRouterParams here - prevents duplicate requests
  }

  // Clean up subscriptions
  override ngOnDestroy() {
    super.ngOnDestroy();
  }
}
